import { z } from 'zod'
import type { Address } from '../addresses/types.ts'
import type { Database, Tables } from '../db/supabase.types.ts'
import type { LatLng } from '../geography/types.ts'
import { type assetDataSchema, type AssetMedia } from './assetDataTypes.ts'
import { type AssetRelationType, type ParsedAssetRelation } from './assetRelationTypes.ts'
import { type sourceDataSchema } from './assetSourceTypes.ts'

export type AssetPrice = Tables<'asset_prices'>

export const priceTypeEnum = z.enum([
  'asking_price',
  'asking_rent',
  'final_price',
  'final_rent',
  'offer_price',
  'offer_rent',
  'estimated_price',
])

export type AssetPriceType = z.infer<typeof priceTypeEnum>

export type RawListingAsset = Omit<
  Database['public']['Tables']['assets']['Row'],
  'feedback_settings'
> & {
  users_assets: Pick<
    Database['public']['Tables']['users_assets']['Row'],
    'id' | 'relation_type' | 'data' | 'user_id'
  >[]
}

export type RawAsset = RawListingAsset & {
  address: Database['public']['Views']['asset_addresses']['Row'][]
  prices?: AssetPrice[]
}

export type ParsedAssetOrganisation = {
  id: string
  name: string
  logoMedia: unknown
  realtorName: string | null
}

export type ParsedAsset = Omit<
  RawAsset,
  'data' | 'source' | 'address' | 'media' | 'feedback_settings' | 'users_assets'
> & {
  data?: z.infer<typeof assetDataSchema> | null
  source: z.infer<typeof sourceDataSchema>
  address: Address | null
  relations: ParsedAssetRelation[]
  media: AssetMedia[]
  prices?: AssetPrice[]
  geom?: LatLng | null
  all_asset_ids?: string[]
  realtor_count?: number
  organisation?: ParsedAssetOrganisation | null
}

export type AssetDetailsResponse = {
  items: ParsedAsset[]
  relationType: string
}

export const pricePayloadSchema = z.object({
  priceValue: z.number(),
  priceType: priceTypeEnum,
})

export type PricePayload = z.infer<typeof pricePayloadSchema>

export const assetPricesPayloadSchema = z.object({
  displayPrice: z.number().optional(),
  finalPrice: z.number().optional(),
})

export type AssetPricesPayload = z.infer<typeof assetPricesPayloadSchema>

export function getDefaultPriceType(relationType: AssetRelationType): AssetPriceType {
  return relationType === 'prospect' ? 'estimated_price' : 'asking_price'
}
